import momentTimezone from 'moment-timezone';
import { useCallback, useEffect, useState } from 'react';
import { getBrowserTimezone } from 'shared-utils';
import { useQuery, useSubscription } from 'urql';

import { TokenLimitCheck } from '../data/TokenLimitCheck.query';
import { TokenLimitUpdatesSubscription } from '../data/TokenLimitUpdates.subscription';

import type { ResultOf } from 'gql.tada';

/** Formats a reset timestamp as a wall-clock time in the viewer's own timezone. */
export const formatResetTime = (timestamp: number): string => {
  const userTimezone = getBrowserTimezone();
  const moment = momentTimezone(timestamp).tz(userTimezone);
  const timeStr = moment.format('h:mma');
  const tzAbbr = moment.format('z');
  return `${timeStr} (${tzAbbr})`;
};

type TokenLimitInfo = {
  allowed: boolean;
  tokensRemaining: number;
  tokensUsed: number;
  windowResetTime: number;
};

type TokenLimitStatus = {
  isAtLimit: boolean;
  isAtWarning: boolean;
  resetTimestamp: number;
  tokensRemaining: number;
  tokensUsed: number;
  usagePercent: number;
};

// Warn if over 90% tokens used
const WARNING_LIMIT = 90;

// Covers a client clock ahead of the server's, and a refetch that fails.
const RESET_RETRY_MS = 60_000;

const getUsagePercent = (limit: TokenLimitInfo) => {
  const total = limit.tokensUsed + limit.tokensRemaining;
  return total > 0 ? (limit.tokensUsed / total) * 100 : 0;
};

const isLimitExceeded = (limit: TokenLimitInfo) => !limit.allowed || limit.tokensRemaining <= 0;

/**
 * Surfaces the team's daily token budget: a 10M cap that resets at midnight, so a
 * team approaching it can act on the warning — space the work out, or wait for the
 * reset they can see coming.
 *
 * The per-minute ceiling is deliberately not surfaced. It recovers in seconds, and
 * because the counter is team-scoped, blocking on it walled off every teammate with
 * the chat open the moment one person ran a large query. A minute rejection is
 * handled where it actually happens instead — the 429 renders an alert with a retry.
 */
export const useTokenLimitCheck = () => {
  const [tokenLimitStatus, setTokenLimitStatus] = useState<TokenLimitStatus>({
    isAtLimit: false,
    isAtWarning: false,
    resetTimestamp: 0,
    tokensRemaining: 0,
    tokensUsed: 0,
    usagePercent: 0,
  });

  const [{ data: queryData, fetching }, refetch] = useQuery({
    query: TokenLimitCheck,
    requestPolicy: 'network-only',
  });

  const updateTokenLimitStatus = useCallback((dailyLimit: TokenLimitInfo) => {
    const usagePercent = Math.round(getUsagePercent(dailyLimit));
    const isAtLimit = isLimitExceeded(dailyLimit);

    setTokenLimitStatus({
      isAtLimit,
      isAtWarning: usagePercent >= WARNING_LIMIT && !isAtLimit,
      resetTimestamp: dailyLimit.windowResetTime,
      tokensRemaining: dailyLimit.tokensRemaining,
      tokensUsed: dailyLimit.tokensUsed,
      usagePercent,
    });
  }, []);

  useSubscription(
    {
      query: TokenLimitUpdatesSubscription,
      pause: fetching,
    },
    (_previous, response: ResultOf<typeof TokenLimitUpdatesSubscription>) => {
      const daily = response?.tokenLimitUpdates?.daily;
      if (daily) {
        updateTokenLimitStatus(daily);
      }
      return response;
    },
  );

  useEffect(() => {
    const daily = queryData?.tokenLimitCheck?.daily;
    if (daily) {
      updateTokenLimitStatus(daily);
    }
  }, [queryData, updateTokenLimitStatus]);

  // Nobody on the team can spend tokens at the cap, so the subscription never fires
  // and the reset has to be polled for.
  const { isAtLimit, resetTimestamp } = tokenLimitStatus;
  useEffect(() => {
    if (!isAtLimit) {
      return;
    }
    let timer: ReturnType<typeof setTimeout>;
    const waitForReset = () => {
      const msUntilReset = resetTimestamp - Date.now();
      if (msUntilReset <= 0) {
        refetch({ requestPolicy: 'network-only' });
        timer = setTimeout(waitForReset, RESET_RETRY_MS);
        return;
      }
      timer = setTimeout(waitForReset, msUntilReset);
    };
    waitForReset();
    return () => clearTimeout(timer);
  }, [isAtLimit, resetTimestamp, refetch]);

  return tokenLimitStatus;
};
